// TanStack Query client + persistence wiring.
//
// What this gives us beyond a plain in-memory cache:
//   - Stale-while-revalidate: cached data renders instantly, refetch in
//     background swaps in fresh data when ready. Critical on flaky mobile
//     networks where the agent's create-order screen used to spinner for
//     8+ seconds while it pulled the whole agent_retailer list.
//   - Persistence to localStorage with TTL: an agent who closes and
//     reopens the browser tab within `maxAge` still gets instant load
//     from cache instead of refetching.
//   - Request deduplication: if two components mount the same query at
//     once, only one network request fires.
//   - Built-in retry with exponential backoff: 3 attempts by default,
//     useful on intermittent connectivity.
//
// Per-query staleTime / gcTime are configured at the useQuery call site
// (see src/hooks/queries/*). The defaults below apply when a query doesn't
// specify its own. We keep defaults conservative (1 min stale, 5 min gc)
// so a query that forgets to set staleTime doesn't accidentally show
// week-old data.

import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Conservative defaults — individual hooks override these.
      staleTime: 60 * 1000, // 1 min
      gcTime: 5 * 60 * 1000, // 5 min — was cacheTime in v4
      retry: 2, // 3 total attempts on transient network failure
      refetchOnWindowFocus: false, // don't refetch every tab-focus, noisy
      refetchOnReconnect: 'always', // do refetch when network returns
    },
    mutations: {
      retry: 0, // never auto-retry mutations
    },
  },
});

// localStorage persister with a 24-hour ceiling. Anything older than that
// is treated as cold on next load — protects against truly stale data
// (e.g. agent comes back the next morning, network changes, retailers
// were reassigned overnight). The persister still respects each query's
// own staleTime within the 24 h window.
export const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: 'orderplus-query-cache-v1',
  throttleTime: 1000, // batch writes to localStorage to once per second
});

// 24 hours — see comment above.
export const PERSIST_MAX_AGE_MS = 24 * 60 * 60 * 1000;
